import { AuditTrail } from '@card-software/nexus-models';
import { SelectOption } from '../common';
import { Roll } from '../knitting/roll';
import { Tags } from '../tags/tags';
import { DyeingMachine } from './dyeing';

export type WeightUom = 'lb' | 'kg' | 'yd';

export type WeightUomOption = SelectOption<WeightUom>;

export interface ProductionCardStatusOption extends SelectOption<ProductionCardStatus> {
  disabled: boolean;
}

export interface ProductionCardSearchCommand {
  dyeingForecastReference?: string;
  productionCardNumber?: string;
  assetId?: string;
  statuses?: number[];
}

export interface ProductionCard extends AuditTrail {
  id?: string;
  productionCardNumber?: string;
  workOrderReference: string;
  status: number;
  dyeingForecastReference?: string;
  colorReference?: string;
  colorName?: string;
  dyeingAssetId?: string;
  productionCardEntries: ProductionCardEntry[];
  tagReferences?: (string | undefined)[];
  // this properties is used for handle into UI please not delete
  showDetails?: boolean;
  machineAssigned?: DyeingMachine;
  qtyToUse?: number;
  storageInAPI?: boolean;
  tags?: Tags[];
  batchingEstimatedDate?: Date | null;
  dyeingEstimatedStartDate?: Date | null;
  finishingEstimatedEndDate?: Date | null;
  rollNumbers: string[]; //list of rolls assigned to this production card
  route?: Route[];
  reprocessNumber?: number;
}

export interface ProductionCardEntry {
  fabricCode: string;
  quantity: number;
}

export enum ProductionCardStatus {
  Draft = 0, // when user is editing the production card (Default)
  Submitted = 1, // when user submits the production card, this will trigger the addition to greige requirement (User)
  Approved = 2, // when the production card is approved by the manager and has an assigned machine to use (User)
  PlanningForBatching = 3,
  InRouteToBatching = 4,
  Batching = 5,
  InRouteToBrd = 6,
  Brd = 7,
  Dyeing = 10,
  ReadyForFinishing = 11,
  Finishing = 12,
  FinishedGood = 13,
  Shipped = 14,
  Closed = 15,
  Reassignment = 16,
  Deleted = 8,
  Rejected = 9,
}

export interface ProductionCardClosePreview {
  rollsForProcessLoss: ClosePreviewRoll[];
  processLossFabricSummary: ClosePreviewFabricSummary[];
  retainedRolls: ClosePreviewRoll[];
  retainedFabricSummary: ClosePreviewFabricSummary[];
  totalLinkedRolls: number;
  totalProcessLossRolls: number;
  totalRetainedRolls: number;
}

export interface ClosePreviewRoll {
  roll: Roll;
  number: string;
  currentLocation: { reference: string; code: string; name: string };
  fabricDescription: string | null;
  fabricForm: number | null;
}

export interface ClosePreviewFabricSummary {
  fabricCode: string;
  fabricDescription: string | null;
  fabricForm: number | null;
  rollCount: number;
  totalWeightInLbs: number;
}

export interface Route {
  lineId: string;
  processId: string;
  dateTimeAdded: null;
  startTime: null;
  endTime: null;
}

export interface FinishedGoodsBalance {
  fabricCode: string;
  longDescription: string;
  type: string;
  totalRolls: number;
  loadedRolls: number;
  missingRolls: number;
  totalWeight: number;
  loadedWeight: number;
}

export interface ProductionCardBalance {
  fabricCode: string;
  longDescription: string;
  type: string;
  planned: number;
  currentBalance: number;
  rollCount: number;
  bundleWeightBalance?: number;
}

export interface ProductionCardTransferCheck {
  originProductionCardReference: string;
  targetProductionCardReference: string;
}

export interface ProductionCardTransferCheckResult {
  rolls: Roll[];
  colorReferenceMatches: boolean;
  fabricCodesCompatible: boolean;
  incompatibleFabricCodes?: string[] | null;
  targetFabricCodes: string[];
}
